/**
 * Aircraft Detail Page
 * Consolidated view of a single aircraft's operational data:
 * status banner, recent flights, upcoming bookings, open defects,
 * maintenance timeline, configuration summary, and fuel/oil gauges.
 */

import {
  getAircraftById,
  getFlightsForAircraft,
  getUpcomingBookings,
  getAllDefectsForAircraft,
  getMaintenanceEvents,
  getCurrentBillingRate,
  getLastFuelOil,
} from '../services/api.js';
import { createDataGrid } from '../components/data-grid.js';
import { createFuelOilGauges } from '../components/fuel-oil-gauges.js';
import { DEFECT_STATUS } from '../utils/constants.js';

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Derive the last known location from the most recent flight's destination.
 * @param {Object[]} flights - Array of flight records sorted by date desc
 * @returns {string} Destination of most recent flight, or 'No flights recorded'
 */
export function deriveLastKnownLocation(flights) {
  if (!flights || flights.length === 0) return 'No flights recorded';
  return flights[0].destination || 'No flights recorded';
}

/**
 * Returns the oil level class: 'adequate' if >= 50% of max capacity, 'low' otherwise.
 * @param {number} oilLevel - Current oil level
 * @param {number} maxCapacity - Maximum oil capacity
 * @returns {'adequate'|'low'}
 */
export function getOilLevelClass(oilLevel, maxCapacity) {
  if (maxCapacity == null || maxCapacity <= 0) return 'low';
  if (oilLevel == null || oilLevel < 0) return 'low';
  return oilLevel >= maxCapacity * 0.5 ? 'adequate' : 'low';
}

/**
 * Returns the hours remaining until the next 50-hour check.
 * @param {number} hoursSinceLast50hr - Hours since last 50-hour check
 * @returns {number}
 */
export function getHoursUntil50HrCheck(hoursSinceLast50hr) {
  if (hoursSinceLast50hr == null || hoursSinceLast50hr < 0) return 50;
  return 50 - hoursSinceLast50hr;
}

/**
 * Transforms raw aircraft data into a display-ready object.
 * @param {Object} aircraft - Raw aircraft record from the database
 * @returns {Object} Display-ready aircraft detail data
 */
export function getAircraftDetailData(aircraft) {
  if (!aircraft) return null;
  const hoursUntil50Hr = getHoursUntil50HrCheck(aircraft.hours_since_last_50hr);
  const oilLevelClass = aircraft.max_oil_capacity != null
    ? getOilLevelClass(aircraft._lastOilLevel ?? 0, aircraft.max_oil_capacity)
    : 'low';
  return {
    name: aircraft.name || '',
    type: aircraft.type || '',
    registration: aircraft.registration || '',
    imageUrl: aircraft.image_url || null,
    totalAirframeHours: aircraft.airframe_hours ?? 0,
    hoursUntil50HrCheck: hoursUntil50Hr,
    hoursSinceLast50hr: aircraft.hours_since_last_50hr ?? 0,
    annualInspectionDueDate: aircraft.last_annual_date || null,
    nextOilChangeHours: aircraft.next_oil_change_hours ?? null,
    status: aircraft.status || 'SERVICEABLE',
    location: aircraft.location || '',
    oilLevelClass,
    maxOilCapacity: aircraft.max_oil_capacity ?? 0,
    oilMeasurementUnit: aircraft.oil_measurement_unit || '',
    fuelTankConfig: aircraft.fuel_tank_config || {},
  };
}

/**
 * Create the Aircraft Status Banner element.
 * @param {Object} aircraft - Aircraft record
 * @param {Object[]} flights - Recent flights (sorted by date desc)
 * @param {Object[]} openDefects - Open defects for the aircraft
 * @returns {HTMLElement}
 */
export function createStatusBanner(aircraft, flights, openDefects) {
  const banner = document.createElement('div');
  banner.className = 'aircraft-status-banner';

  const isUnserviceable = aircraft.status === 'UNSERVICEABLE';
  const statusClass = isUnserviceable ? 'red' : 'green';

  // Status indicator (overall serviceability — green/red)
  const indicator = document.createElement('div');
  indicator.className = `status-banner__indicator status-banner__indicator--${statusClass}`;
  indicator.textContent = aircraft.status;
  banner.appendChild(indicator);

  // Aircraft picture
  if (aircraft.image_url) {
    const img = document.createElement('img');
    img.className = 'status-banner__image';
    img.src = aircraft.image_url;
    img.alt = `${aircraft.name || aircraft.registration}`;
    banner.appendChild(img);
  }

  // Registration, type, name
  const info = document.createElement('div');
  info.className = 'status-banner__info';

  const regSpan = document.createElement('span');
  regSpan.className = 'status-banner__registration';
  regSpan.textContent = aircraft.registration;
  info.appendChild(regSpan);

  const typeSpan = document.createElement('span');
  typeSpan.className = 'status-banner__type';
  typeSpan.textContent = aircraft.type;
  info.appendChild(typeSpan);

  const nameSpan = document.createElement('span');
  nameSpan.className = 'status-banner__name';
  nameSpan.textContent = aircraft.name;
  info.appendChild(nameSpan);

  banner.appendChild(info);

  // Component hours
  const hours = document.createElement('div');
  hours.className = 'status-banner__hours';

  const hoursUntil50Hr = getHoursUntil50HrCheck(aircraft.hours_since_last_50hr);

  for (const [label, value] of [
    ['Airframe', aircraft.airframe_hours ?? 0],
    ['Engine', aircraft.engine_hours ?? 0],
    ['Propeller', aircraft.propeller_hours ?? 0],
    ['50hr Check', `${hoursUntil50Hr.toFixed(1)} remaining`],
  ]) {
    const span = document.createElement('span');
    span.textContent = `${label}: ${typeof value === 'number' ? value + ' hrs' : value}`;
    hours.appendChild(span);
  }
  banner.appendChild(hours);

  // Maintenance status: annual inspection + oil change
  const maint = document.createElement('div');
  maint.className = 'status-banner__maintenance';

  const annualSpan = document.createElement('span');
  annualSpan.className = 'status-banner__annual';
  annualSpan.textContent = `Annual Inspection Due: ${aircraft.last_annual_date || 'N/A'}`;
  maint.appendChild(annualSpan);

  const oilChangeSpan = document.createElement('span');
  oilChangeSpan.className = 'status-banner__oil-change';
  oilChangeSpan.textContent = `Next Oil Change: ${aircraft.next_oil_change_hours != null ? aircraft.next_oil_change_hours + ' hrs' : 'N/A'}`;
  maint.appendChild(oilChangeSpan);

  banner.appendChild(maint);

  // Last known location
  const location = document.createElement('div');
  location.className = 'status-banner__location';
  const lastLocation = deriveLastKnownLocation(flights);
  location.textContent = `Last Known Location: ${lastLocation}`;
  banner.appendChild(location);

  // Defect count badge
  const defectCount = openDefects ? openDefects.filter(d => d.status === 'OPEN').length : 0;
  if (defectCount > 0) {
    const badge = document.createElement('span');
    badge.className = 'status-banner__defect-badge';
    badge.textContent = `${defectCount} open defect${defectCount !== 1 ? 's' : ''}`;
    banner.appendChild(badge);
  }

  return banner;
}

// ---------------------------------------------------------------------------
// Data grid helper functions (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Format minutes as HH:MM string.
 */
function formatMinutes(mins) {
  if (mins == null) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

/**
 * Prepare recent flights data: sort by date desc, limit to 20.
 * @param {Object[]} flights - Raw flight records
 * @returns {Object[]} Processed flights
 */
export function prepareRecentFlights(flights) {
  if (!flights || flights.length === 0) return [];
  return [...flights]
    .sort((a, b) => b.flight_date.localeCompare(a.flight_date))
    .slice(0, 20);
}

/**
 * Filter and sort upcoming bookings: future SUBMITTED or APPROVED, ascending.
 * @param {Object[]} bookings - Raw booking records
 * @param {string} [today] - Today's date string (YYYY-MM-DD), defaults to current date
 * @returns {Object[]} Filtered and sorted bookings
 */
export function prepareUpcomingBookings(bookings, today) {
  if (!bookings || bookings.length === 0) return [];
  const todayStr = today || new Date().toISOString().split('T')[0];
  return [...bookings]
    .filter(b => (b.status === 'SUBMITTED' || b.status === 'APPROVED' || b.status === 'CONFIRMED') && b.flight_date >= todayStr)
    .sort((a, b) => {
      const dateCmp = a.flight_date.localeCompare(b.flight_date);
      if (dateCmp !== 0) return dateCmp;
      return (a.start_time || '').localeCompare(b.start_time || '');
    });
}

/**
 * Sort all defects by opened_at desc. Includes all statuses (OPEN, UNSURE, RESOLVED).
 * @param {Object[]} defects - Raw defect records
 * @returns {Object[]} Sorted defects
 */
export function prepareOpenDefects(defects) {
  if (!defects || defects.length === 0) return [];
  return [...defects]
    .sort((a, b) => (b.opened_at || '').localeCompare(a.opened_at || ''));
}

/**
 * Check if a maintenance event is "Due Soon".
 * Due Soon when next_due_hours - airframe_hours is between 0 and 5 (inclusive).
 * @param {Object} event - Maintenance event with next_due_hours
 * @param {number} airframeHours - Current airframe hours
 * @returns {boolean}
 */
export function isDueSoon(event, airframeHours) {
  if (event.next_due_hours == null || airframeHours == null) return false;
  const diff = event.next_due_hours - airframeHours;
  return diff >= 0 && diff <= 5;
}

/**
 * Derive the current billing rate from a set of rates.
 * Returns the rate with the most recent effective_from <= today.
 * @param {Object[]} rates - Array of billing rate records
 * @param {string} [today] - Today's date string (YYYY-MM-DD)
 * @returns {Object|null} The current rate, or null
 */
export function deriveCurrentBillingRate(rates, today) {
  if (!rates || rates.length === 0) return null;
  const todayStr = today || new Date().toISOString().split('T')[0];
  const eligible = rates
    .filter(r => r.effective_from <= todayStr)
    .sort((a, b) => b.effective_from.localeCompare(a.effective_from));
  return eligible.length > 0 ? eligible[0] : null;
}

// ---------------------------------------------------------------------------
// Section builders
// ---------------------------------------------------------------------------

/**
 * Create the Recent Flights data grid section.
 */
function createRecentFlightsSection(flights) {
  const section = document.createElement('div');
  section.className = 'aircraft-detail__section';

  const title = document.createElement('h3');
  title.textContent = 'Recent Flights';
  section.appendChild(title);

  const prepared = prepareRecentFlights(flights);

  const grid = createDataGrid({
    columns: [
      { key: 'flight_date', label: 'Date' },
      { key: 'pilot_name', label: 'Pilot', format: (_v, row) => row.pilots?.name || row.pilot_name || '' },
      { key: 'departure', label: 'Departure' },
      { key: 'destination', label: 'Destination' },
      { key: 'block_time_mins', label: 'Block Time', format: v => formatMinutes(v) },
      { key: 'flight_time_mins', label: 'Flight Time', format: v => formatMinutes(v) },
    ],
    data: prepared,
    defaultSort: 'flight_date',
    defaultOrder: 'desc',
    emptyMessage: 'No flights recorded for this aircraft',
    filterable: false,
  });

  section.appendChild(grid);
  return section;
}

/**
 * Create the Upcoming Bookings data grid section.
 */
function createUpcomingBookingsSection(bookings) {
  const section = document.createElement('div');
  section.className = 'aircraft-detail__section';

  const title = document.createElement('h3');
  title.textContent = 'Upcoming Bookings';
  section.appendChild(title);

  const prepared = prepareUpcomingBookings(bookings);

  const grid = createDataGrid({
    columns: [
      { key: 'flight_date', label: 'Date' },
      { key: 'start_time', label: 'Start Time' },
      { key: 'end_time', label: 'End Time' },
      { key: 'pilot_name', label: 'Pilot', format: (_v, row) => row.pilots?.name || row.pilot_name || '' },
      { key: 'status', label: 'Status' },
    ],
    data: prepared,
    defaultSort: 'flight_date',
    defaultOrder: 'asc',
    emptyMessage: 'No upcoming bookings',
    filterable: false,
  });

  section.appendChild(grid);
  return section;
}

/**
 * Create the Open Defects data grid section.
 */
/**
 * Returns the CSS class for a defect status badge.
 * @param {string} status - OPEN, UNSURE, or RESOLVED
 * @returns {string}
 */
export function getDefectStatusBadgeClass(status) {
  switch (status) {
    case 'OPEN': return 'defect-badge--open';
    case 'UNSURE': return 'defect-badge--unsure';
    case 'RESOLVED': return 'defect-badge--resolved';
    default: return 'defect-badge--open';
  }
}

/**
 * Create the Defects data grid section with three-state status badges.
 */
function createOpenDefectsSection(defects) {
  const section = document.createElement('div');
  section.className = 'aircraft-detail__section';

  const title = document.createElement('h3');
  title.textContent = 'Defects';
  section.appendChild(title);

  const prepared = prepareOpenDefects(defects);

  const grid = createDataGrid({
    columns: [
      { key: 'title', label: 'Title', format: (v) => v || '' },
      { key: 'description', label: 'Description' },
      { key: 'status', label: 'Status', format: (v) => {
        const badgeClass = getDefectStatusBadgeClass(v);
        return `<span class="defect-badge ${badgeClass}">${(v || 'OPEN').toLowerCase()}</span>`;
      }},
      { key: 'reported_by', label: 'Reported By', format: (_v, row) => row.pilots?.name || row.reported_by || '' },
      { key: 'opened_at', label: 'Date Opened' },
    ],
    data: prepared,
    defaultSort: 'opened_at',
    defaultOrder: 'desc',
    emptyMessage: 'No defects recorded',
    filterable: false,
  });

  section.appendChild(grid);
  return section;
}

/**
 * Create the Maintenance Timeline section.
 */
function createMaintenanceSection(events, airframeHours) {
  const section = document.createElement('div');
  section.className = 'aircraft-detail__section';

  const title = document.createElement('h3');
  title.textContent = 'Maintenance';
  section.appendChild(title);

  if (!events || events.length === 0) {
    const empty = document.createElement('p');
    empty.textContent = 'No maintenance records';
    section.appendChild(empty);
    return section;
  }

  const today = new Date().toISOString().split('T')[0];

  const list = document.createElement('div');
  list.className = 'maintenance-timeline';

  for (const evt of events) {
    const item = document.createElement('div');
    item.className = 'maintenance-timeline__item';

    // Visually distinguish upcoming events
    if (evt.next_due_date && evt.next_due_date > today) {
      item.classList.add('maintenance-timeline__item--upcoming');
    }

    item.innerHTML = `
      <span class="maintenance-timeline__type">${evt.type || ''}</span>
      <span class="maintenance-timeline__description">${evt.description || ''}</span>
      <span class="maintenance-timeline__date">Performed: ${evt.date_performed || ''}</span>
      <span class="maintenance-timeline__hours">Hours: ${evt.hours_at_event ?? ''}</span>
      <span class="maintenance-timeline__next-hours">Next Due: ${evt.next_due_hours ?? 'N/A'} hrs</span>
      <span class="maintenance-timeline__next-date">Next Date: ${evt.next_due_date || 'N/A'}</span>
    `;

    // Due Soon warning
    if (isDueSoon(evt, airframeHours)) {
      const warning = document.createElement('span');
      warning.className = 'maintenance-timeline__due-soon';
      warning.textContent = 'Due Soon';
      item.appendChild(warning);
    }

    list.appendChild(item);
  }

  section.appendChild(list);
  return section;
}

/**
 * Create the Configuration Summary panel.
 */
function createConfigSummarySection(aircraft, billingRate) {
  const section = document.createElement('div');
  section.className = 'aircraft-detail__section';

  const title = document.createElement('h3');
  title.textContent = 'Configuration';
  section.appendChild(title);

  const details = document.createElement('div');
  details.className = 'config-summary';

  // Fuel tanks
  const tankConfig = aircraft.fuel_tank_config || {};
  const tankNames = Object.values(tankConfig);
  const tanksText = tankNames.length > 0 ? tankNames.join(', ') : 'None configured';

  // Oil
  const oilText = `${aircraft.max_oil_capacity ?? 'N/A'} ${aircraft.oil_measurement_unit || ''}`.trim();

  // Component hours
  const hoursText = `Airframe: ${aircraft.airframe_hours ?? 0} | Engine: ${aircraft.engine_hours ?? 0} | Propeller: ${aircraft.propeller_hours ?? 0}`;

  // Billing rate
  const rateText = billingRate
    ? `$${billingRate.rate_per_hour}/hr (from ${billingRate.effective_from})`
    : 'No rate configured';

  details.innerHTML = `
    <div><strong>Fuel Tanks:</strong> ${tanksText}</div>
    <div><strong>Max Oil:</strong> ${oilText}</div>
    <div><strong>Component Hours:</strong> ${hoursText}</div>
    <div><strong>Billing Rate:</strong> ${rateText}</div>
  `;

  section.appendChild(details);
  return section;
}

/**
 * Create the Fuel & Oil Gauges section.
 */
/**
 * Create the Fuel & Oil Gauges section with oil level indicator.
 */
function createFuelOilSection(aircraft, fuelOilRecord, lastFlightDate) {
  const container = document.createElement('div');
  container.className = 'aircraft-detail__section';

  // Fuel & Oil gauges from the reusable component
  container.appendChild(createFuelOilGauges({
    aircraft,
    fuelOilRecord,
    lastFlightDate,
  }));

  // Oil level indicator (green = adequate, amber = low)
  if (fuelOilRecord && fuelOilRecord.oil_level != null) {
    const oilIndicator = document.createElement('div');
    oilIndicator.className = 'oil-level-indicator';

    const oilClass = getOilLevelClass(fuelOilRecord.oil_level, aircraft.max_oil_capacity ?? 0);
    const indicatorColor = oilClass === 'adequate' ? 'green' : 'amber';

    oilIndicator.innerHTML = `
      <span class="oil-level-indicator__dot oil-level-indicator__dot--${indicatorColor}"></span>
      <span class="oil-level-indicator__label">Oil Level: ${fuelOilRecord.oil_level} ${aircraft.oil_measurement_unit || ''} (${oilClass})</span>
    `;
    container.appendChild(oilIndicator);
  }

  return container;
}

// ---------------------------------------------------------------------------
// Aircraft Detail Modal
// ---------------------------------------------------------------------------

/** @type {HTMLElement|null} */
let _activeAircraftModal = null;

function _closeAircraftModal() {
  if (!_activeAircraftModal) return;
  _activeAircraftModal.remove();
  _activeAircraftModal = null;
  document.removeEventListener('keydown', _handleAircraftModalKey);
}

function _handleAircraftModalKey(e) {
  if (e.key === 'Escape') { e.preventDefault(); _closeAircraftModal(); }
}

/**
 * Open a full-detail aircraft modal from the aircraft list.
 * @param {string} aircraftId
 */
export async function openAircraftDetailModal(aircraftId) {
  if (_activeAircraftModal) _closeAircraftModal();

  const backdrop = document.createElement('div');
  backdrop.className = 'detail-modal__backdrop';
  backdrop.setAttribute('role', 'dialog');
  backdrop.setAttribute('aria-modal', 'true');

  const modal = document.createElement('div');
  modal.className = 'detail-modal detail-modal--wide';

  // Header
  const header = document.createElement('div');
  header.className = 'detail-modal__header';
  const titleEl = document.createElement('h2');
  titleEl.className = 'detail-modal__title';
  titleEl.textContent = 'Loading…';
  header.appendChild(titleEl);
  modal.appendChild(header);

  // Body
  const body = document.createElement('div');
  body.className = 'detail-modal__body';
  body.innerHTML = '<p>Loading aircraft details…</p>';
  modal.appendChild(body);

  // Footer
  const footer = document.createElement('div');
  footer.className = 'detail-modal__footer';
  const spacer = document.createElement('div');
  spacer.className = 'detail-modal__footer-spacer';
  footer.appendChild(spacer);
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'btn btn--primary';
  closeBtn.textContent = 'Close';
  closeBtn.addEventListener('click', _closeAircraftModal);
  footer.appendChild(closeBtn);
  modal.appendChild(footer);

  backdrop.appendChild(modal);
  backdrop.addEventListener('click', (e) => { if (e.target === backdrop) _closeAircraftModal(); });

  _activeAircraftModal = backdrop;
  document.body.appendChild(backdrop);
  document.addEventListener('keydown', _handleAircraftModalKey);
  closeBtn.focus();

  try {
    const aircraft = await getAircraftById(aircraftId);
    if (!aircraft) {
      body.innerHTML = '<p class="text-danger">Aircraft not found.</p>';
      return;
    }

    titleEl.textContent = `${aircraft.registration}${aircraft.name ? ' — ' + aircraft.name : ''}`;
    backdrop.setAttribute('aria-label', titleEl.textContent);

    const [flights, bookings, defects, maintenanceEvents, billingRate, lastFuelOil] =
      await Promise.all([
        getFlightsForAircraft(aircraftId).catch(() => []),
        getUpcomingBookings(aircraftId).catch(() => []),
        getAllDefectsForAircraft(aircraftId).catch(() => []),
        getMaintenanceEvents(aircraftId).catch(() => []),
        getCurrentBillingRate(aircraftId).catch(() => null),
        getLastFuelOil(aircraftId).catch(() => null),
      ]);

    body.innerHTML = '';
    body.appendChild(createStatusBanner(aircraft, flights, defects));
    body.appendChild(createRecentFlightsSection(flights));
    body.appendChild(createUpcomingBookingsSection(bookings));
    body.appendChild(createOpenDefectsSection(defects));
    body.appendChild(createMaintenanceSection(maintenanceEvents, aircraft.airframe_hours));
    body.appendChild(createConfigSummarySection(aircraft, billingRate));
    const lastFlightDate = flights.length > 0 ? flights[0].flight_date : null;
    body.appendChild(createFuelOilSection(aircraft, lastFuelOil, lastFlightDate));
  } catch (err) {
    body.innerHTML = '<p class="text-danger">Failed to load aircraft details.</p>';
    console.error('Failed to load aircraft detail modal:', err);
  }
}

// ---------------------------------------------------------------------------
// Main render function
// ---------------------------------------------------------------------------

/**
 * Render the Aircraft Detail Page.
 * @param {{ params: { id: string } }} context - Route context with aircraft ID
 */
export async function renderAircraftDetail({ params }) {
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="container">
      <div id="aircraft-detail-container"><p>Loading aircraft details…</p></div>
    </div>
  `;

  const container = document.getElementById('aircraft-detail-container');
  if (!container) return;

  try {
    const aircraft = await getAircraftById(params.id);

    if (!aircraft) {
      container.innerHTML = `
        <div class="aircraft-not-found">
          <h2>Aircraft not found</h2>
          <p>The requested aircraft could not be found.</p>
          <a href="#/aircraft" class="btn btn--primary">Back to Aircraft List</a>
        </div>
      `;
      return;
    }

    // Fetch all data in parallel
    const [flights, bookings, defects, maintenanceEvents, billingRate, lastFuelOil] =
      await Promise.all([
        getFlightsForAircraft(params.id).catch(() => []),
        getUpcomingBookings(params.id).catch(() => []),
        getAllDefectsForAircraft(params.id).catch(() => []),
        getMaintenanceEvents(params.id).catch(() => []),
        getCurrentBillingRate(params.id).catch(() => null),
        getLastFuelOil(params.id).catch(() => null),
      ]);

    container.innerHTML = '';

    // Back link
    const backLink = document.createElement('a');
    backLink.href = '#/aircraft';
    backLink.className = 'aircraft-detail__back';
    backLink.textContent = '← Back to Aircraft List';
    container.appendChild(backLink);

    // Status Banner
    container.appendChild(createStatusBanner(aircraft, flights, defects));

    // Recent Flights
    container.appendChild(createRecentFlightsSection(flights));

    // Upcoming Bookings
    container.appendChild(createUpcomingBookingsSection(bookings));

    // Open Defects
    container.appendChild(createOpenDefectsSection(defects));

    // Maintenance Timeline
    container.appendChild(createMaintenanceSection(maintenanceEvents, aircraft.airframe_hours));

    // Configuration Summary
    container.appendChild(createConfigSummarySection(aircraft, billingRate));

    // Fuel & Oil Gauges
    const lastFlightDate = flights.length > 0 ? flights[0].flight_date : null;
    container.appendChild(createFuelOilSection(aircraft, lastFuelOil, lastFlightDate));

  } catch (err) {
    container.innerHTML = `
      <div class="aircraft-not-found">
        <h2>Aircraft not found</h2>
        <p>The requested aircraft could not be found.</p>
        <a href="#/aircraft" class="btn btn--primary">Back to Aircraft List</a>
      </div>
    `;
    console.error('Failed to load aircraft detail:', err);
  }
}
